import React, { useMemo, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useSelector } from "react-redux";
import { selectAuth } from "@/redux/auth/selectors";
import { Button, Pagination } from "antd";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// --- MultiMeasureChart Component ---
const MultiMeasureChart = ({ labels, datasetsConfig, title }) => {
  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: !!title,
        text: title || "",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
    interaction: {
      mode: "index",
      intersect: false,
    },
  };

  const chartData = {
    labels,
    datasets: datasetsConfig,
  };

  return (
    <div>
      <Bar options={options} data={chartData} />
    </div>
  );
};

// --- MultiMeasure Component with Pagination ---
const MultiMeasure = ({ initiatives }) => {
  const currentUser = useSelector(selectAuth);
  const [currentPage, setCurrentPage] = useState(1);
  const chartsPerPage = 15;

  // Define labels once
  const labels = useMemo(
    () => [
      "FY 2023/24",
      "FY 2024/25",
      "FY 2025/26",
      "FY 2026/27",
      "FY 2027/28",
    ],
    []
  );

  // 🚀 Performance Optimization: Wrap the expensive data transformation in useMemo.
  const transformedData = useMemo(() => {
    if (!initiatives || !currentUser) {
      return [];
    }

    const currentUserId = currentUser.id;
    const special = currentUser.hasSpecialAccess === true;
    const isAdmin = ["CTO", "TO Finance", "Strategy Analyst", "Admin"].includes(
      currentUser.role?.name
    );

    // 1. Filter the initiatives
    const filteredInitiatives = initiatives.filter((initiative) => {
      // Handle cases where currentUser or its properties might be missing
      if (!currentUserId) return false;

      const isOwner =
        initiative.initiativeOwnerInfo?.[0]?._id === currentUserId;
      const isWorkstreamLead =
        initiative.workstreamLeadInfo?.[0]?._id === currentUserId;
      const isWorkstreamSponsor =
        initiative.workstreamSponsorInfo?.[0]?._id === currentUserId;
      const isAllowedUser = initiative.AllowOnlyTo?.some(
        (allowedUser) => allowedUser._id === currentUserId
      );

      return (
        isOwner ||
        isWorkstreamLead ||
        isWorkstreamSponsor ||
        isAllowedUser ||
        isAdmin ||
        special
      );
    });

    // 2. Map and transform the filtered data
    return filteredInitiatives.map((initiative) => {
      // Initialize all annual arrays with 5 zeros
      const initialArray = () => [0, 0, 0, 0, 0];

      let recurringBenefitPlan = initialArray();
      let recurringBenefitActual = initialArray();
      let negativeRecurringImpactPlan = initialArray();
      let negativeRecurringImpactActual = initialArray();
      let implementationCostPlan = initialArray();
      let implementationCostActual = initialArray();
      let oneTimeBenefitPlan = initialArray();
      let oneTimeBenefitActual = initialArray();

      initiative.impact?.forEach((impactItem) => {
        // Use optional chaining for safety
        const { frequencyAndDirection, annuallyPlan, annuallyActual } =
          impactItem;

        // Helper function to safely update the arrays
        const updateArrays = (planArray, actualArray) => {
          for (let i = 0; i < 5; i++) {
            planArray[i] += annuallyPlan?.[i] || 0;
            actualArray[i] += annuallyActual?.[i] || 0;
          }
        };

        switch (frequencyAndDirection) {
          case "Recurring benefits":
            updateArrays(recurringBenefitPlan, recurringBenefitActual);
            break;
          case "Recurring Negative Impact (ETB)":
            updateArrays(
              negativeRecurringImpactPlan,
              negativeRecurringImpactActual
            );
            break;
          case "One Time Benefit":
            updateArrays(oneTimeBenefitPlan, oneTimeBenefitActual);
            break;
          case "Implementation Cost":
            updateArrays(implementationCostPlan, implementationCostActual);
            break;
          default:
            break;
        }
      });

      // Calculate PBT (Profit Before Tax/Implementation Cost)
      const pbtPlan = recurringBenefitPlan.map(
        (value, index) =>
          value +
          oneTimeBenefitPlan[index] -
          negativeRecurringImpactPlan[index]
      );
      const pbtActual = recurringBenefitActual.map(
        (value, index) =>
          value +
          oneTimeBenefitActual[index] -
          negativeRecurringImpactActual[index]
      );

      return {
        initiativeName: initiative.initiativeName,
        recurringBenefitPlan,
        recurringBenefitActual,
        implementationCostPlan,
        implementationCostActual,
        oneTimeBenefitPlan,
        oneTimeBenefitActual,
        pbtPlan,
        pbtActual,
      };
    });
  }, [initiatives, currentUser]);

  // --- Pagination Logic ---
  const totalCharts = transformedData.length;
  const totalPages = Math.ceil(totalCharts / chartsPerPage);

  // Get current page charts
  const currentCharts = useMemo(() => {
    const startIndex = (currentPage - 1) * chartsPerPage;
    const endIndex = startIndex + chartsPerPage;
    return transformedData.slice(startIndex, endIndex);
  }, [transformedData, currentPage, chartsPerPage]);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // --- Render ---
  if (transformedData.length === 0) {
    return <div>No accessible initiatives.</div>;
  }

  return (
    <div style={{ width: "100%", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Charts Display */}
      {currentCharts.map((initiativeData, index) => (
        <div key={`${initiativeData.initiativeName}-${index}`} style={{ marginBottom: "40px" }}>
          <MultiMeasureChart
            labels={labels}
            datasetsConfig={[
              {
                label: "Recurring Benefits (Planned)",
                data: initiativeData.recurringBenefitPlan,
                backgroundColor: "rgba(53, 162, 235, 0.5)",
                type: "bar",
                yAxisID: "y",
              },
              {
                label: "Recurring Benefits (Actual)",
                data: initiativeData.recurringBenefitActual,
                backgroundColor: "rgba(53, 162, 235, 1)",
                type: "bar",
                yAxisID: "y",
              },
              {
                label: "One Time Benefit (Planned)",
                data: initiativeData.oneTimeBenefitPlan,
                backgroundColor: "rgba(75, 192, 192, 0.5)",
                type: "bar",
                yAxisID: "y",
              },
              {
                label: "One Time Benefit (Actual)",
                data: initiativeData.oneTimeBenefitActual,
                backgroundColor: "rgba(75, 192, 192, 1)",
                type: "bar",
                yAxisID: "y",
              },
              {
                label: "Implementation Cost (Planned)",
                data: initiativeData.implementationCostPlan,
                backgroundColor: "rgba(255, 99, 132, 0.5)",
                type: "bar",
                yAxisID: "y",
              },
              {
                label: "Implementation Cost (Actual)",
                data: initiativeData.implementationCostActual,
                backgroundColor: "rgba(255, 99, 132, 1)",
                type: "bar",
                yAxisID: "y",
              },
              {
                label: "PBT (Planned)",
                data: initiativeData.pbtPlan,
                borderColor: "rgb(50, 205, 50)",
                backgroundColor: "rgba(50, 205, 50, 0.5)",
                type: "line",
                yAxisID: "y",
              },
              {
                label: "PBT (Actual)",
                data: initiativeData.pbtActual,
                borderColor: "rgb(50, 205, 50)",
                backgroundColor: "rgba(50, 205, 50, 1)",
                type: "line",
                yAxisID: "y",
              },
            ]}
            title={initiativeData.initiativeName}
          />
        </div>
      ))}

      {/* Pagination Controls */}
      <div
        style={{
          marginTop: "30px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        {/* Custom Pagination Buttons */}
        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
          <Button
            type="primary"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            ◀ Previous
          </Button>

          <span style={{ fontSize: "14px", fontWeight: "bold" }}>
            Page {currentPage} of {totalPages}
          </span>

          <Button
            type="primary"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            Next ▶
          </Button>
        </div>

        {/* Ant Design Pagination */}
        <Pagination
          current={currentPage}
          total={totalCharts}
          pageSize={chartsPerPage}
          onChange={handlePageChange}
          showSizeChanger={false}
          showQuickJumper
          showTotal={(total, range) =>
            `${range[0]}-${range[1]} of ${total} initiatives`
          }
        />

        {/* Page Info */}
        <div
          style={{
            textAlign: "center",
            color: "#666",
            fontSize: "14px",
          }}
        >
          Showing initiatives{" "}
          {(currentPage - 1) * chartsPerPage + 1} to{" "}
          {Math.min(currentPage * chartsPerPage, totalCharts)} of{" "}
          {totalCharts} total
        </div>
      </div>
    </div>
  );
};

export default MultiMeasure;